import { AxiosResponse } from 'axios';

import { axiosInstance } from '@/shared/api/axios';
import {
  ComplianceExtractDataRequestType,
  ComplianceExtractDataResponseType,
  DownloadExcelComplianceRequestType,
  DownloadExcelDataRequestType,
  DownloadExcelFileUrlResponseType,
  DownloadExtractionDataPdfRequestType,
  GetDataExtractionRequestType,
  GetDataExtractionResponseType,
  PreviewRequestType,
  PreviewResponseType,
  PutDataExtractionRequestType,
  RegisterDataExtractionRequestType,
  RegisterDataExtractionResponseType,
} from '@/shared/types/api/registry/api.dataExtraction';

export const downloadExtractionDataPdf = async ({
  fileIds,
}: DownloadExtractionDataPdfRequestType): Promise<AxiosResponse<Blob>> => {
  return axiosInstance.post<Blob>(
    `/data/extraction/pdf/downloads/`,
    { fileIds },
    {
      responseType: 'blob',
    },
  );
};

export const downloadToukiPdf = async ({
  fileIds,
}: DownloadExtractionDataPdfRequestType): Promise<AxiosResponse<Blob>> => {
  return axiosInstance.post<Blob>(
    `/touki/download/pdf`,
    { fileIds },
    {
      responseType: 'blob',
    },
  );
};

export const downloadExcelData = async ({
  groupId,
  companyInformationIds,
}: DownloadExcelDataRequestType): Promise<Blob> => {
  const { data } = await axiosInstance.post<Blob>(
    `/data/extraction/exceldata/`,
    {
      groupId,
      companyInformationIds,
    },
    {
      responseType: 'blob',
    },
  );

  return data;
};

// ROBO_COMPLIANCE-130対応で登記簿側がS3保存＋署名付きURLを返すようになったため、
// バイナリ（blob）ではなく {fileName, url} のJSONを返す。取得後はurlから実体をダウンロードすること。
export const downloadToukiExcel = async ({
  groupId,
  companyInformationIds,
}: DownloadExcelDataRequestType): Promise<DownloadExcelFileUrlResponseType> => {
  const { data } = await axiosInstance.post<DownloadExcelFileUrlResponseType>(
    `/touki/download/excel`,
    {
      groupId,
      companyInformationIds,
    },
  );
  return data;
};

export const downloadToukiComplianceExcel = async ({
  groupId,
  companyInformationIds,
}: DownloadExcelComplianceRequestType): Promise<DownloadExcelFileUrlResponseType> => {
  const { data } = await axiosInstance.post<DownloadExcelFileUrlResponseType>(
    `/touki/download/compliance`,
    {
      groupId,
      companyInformationIds,
    },
  );
  return data;
};

export const downloadExcelCompliance = async ({
  groupId,
  companyInformationIds,
}: DownloadExcelComplianceRequestType): Promise<Blob> => {
  const { data } = await axiosInstance.post<Blob>(
    `/data/extraction/excel/compliance/`,
    {
      groupId,
      companyInformationIds,
    },
    {
      responseType: 'blob',
    },
  );

  return data;
};

export const getDataExtraction = async ({
  companyInformationId,
}: GetDataExtractionRequestType): Promise<GetDataExtractionResponseType> => {
  const { data } = await axiosInstance.get<GetDataExtractionResponseType>(
    `/touki/extraction/data`,
    {
      params: {
        companyInformationId,
      },
    },
  );

  return data;
};

export const getPreview = async ({ fileId }: PreviewRequestType): Promise<PreviewResponseType> => {
  const { data } = await axiosInstance.get<PreviewResponseType>(`/touki/extraction/preview`, {
    params: {
      fileId,
    },
  });

  return data;
};

export const putDataExtraction = async (
  requestBody: PutDataExtractionRequestType,
): Promise<void> => {
  await axiosInstance.put<void>(`/touki/extraction/data`, {
    companyInformationId: requestBody.companyInformationId,
    groupId: requestBody.groupId,
    updatedAt: requestBody.updatedAt,
    company: {
      fileId: requestBody.company.fileId,
      toukiDate: requestBody.company.toukiDate,
      corporateNumber: requestBody.company.corporateNumber,
      companyName: requestBody.company.companyName,
      representativeName: requestBody.company.representativeName,
      address: requestBody.company.address,
      dateOfEstablishment: requestBody.company.dateOfEstablishment,
      capitalStock: requestBody.company.capitalStock,
      extractionDate: requestBody.company.extractionDate,
    },
    executives: requestBody.executives.map((exec) => ({
      registryExecutiveId: exec.registryExecutiveId ?? null,
      executiveId: exec.executiveId,
      name: exec.name,
      startDate: exec.startDate,
      endDate: exec.endDate,
      registrationReasonId: exec.registrationReasonId,
    })),
  });
};

export const registerDataExtraction = async (
  requestBody: RegisterDataExtractionRequestType,
): Promise<RegisterDataExtractionResponseType> => {
  const { data } = await axiosInstance.post<RegisterDataExtractionResponseType>(
    `/data/extraction/`,
    {
      userId: requestBody.userId,
      groupId: requestBody.groupId,
      company: {
        fileId: requestBody.company.fileId,
        corporateNumber: requestBody.company.corporateNumber,
        companyName: requestBody.company.companyName,
        representativeName: requestBody.company.representativeName,
        address: requestBody.company.address,
        dateOfEstablishment: requestBody.company.dateOfEstablishment,
        capitalStock: requestBody.company.capitalStock,
      },
      executives: requestBody.executives.map((exec) => ({
        executiveId: exec.executiveId,
        name: exec.name,
        startDate: exec.startDate,
        endDate: exec.endDate,
        registrationReasonId: exec.registrationReasonId,
      })),
    },
  );

  return data;
};

export const complianceExtractData = async (
  requestBody: ComplianceExtractDataRequestType,
): Promise<ComplianceExtractDataResponseType> => {
  const { data } = await axiosInstance.post<ComplianceExtractDataResponseType>(
    `/touki/extraction/compliance/execute`,
    {
      groupId: requestBody.groupId,
      fileIds: requestBody.fileIds,
      executeType: requestBody.executeType,
      launchType: requestBody.launchType,
      complianceExecutionHistoryIds: requestBody.complianceExecutionHistoryIds,
      complianceUserId: requestBody.complianceUserId,
      isManualUpload: requestBody.isManualUpload,
    },
  );

  return data;
};
